import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useTaskTypes } from "components/Actions/dialogs/Devices/CreateTask/hooks/useTaskTypes";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import {
    getFilteredCompaniesIds,
    handleFilterOptionsStructure
} from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { ENDPOINT_PATH_SEGMENTS, FILTER_NAMES } from "shared/types/enums";
import { Filter } from "shared/types/interfaces";

export const useFilterDropdown = (
    filterName: FILTER_NAMES,
    customTableName?: string,
    additionalParams?: string,
    shouldIgnoreCompanySelector?: boolean
) => {
    const { queryParams, companyFilteredValues } = useContext(ParamsContext);
    const { tableName } = useContext(TableContext);

    const { t } = useTranslation();

    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const { tasks } = useTaskTypes();

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<Filter[]>([]);

    const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

    useEffect(() => {
        let isSubscribed = true;

        if (isOpen) {
            getFilterValues(isSubscribed);
        }

        return () => {
            isSubscribed = false;
            cancelSource.cancel(API_IS_BEING_CANCELLED);
            setOptions([]);
            setIsLoading(false);
        };

        // eslint-disable-next-line
    }, [isOpen]);

    const getFilterValues = async (isSubscribed: boolean) => {
        try {
            setIsLoading(true);

            const filteredCompanies = getFilteredCompaniesIds(
                companyFilteredValues
            );

            const rootCompanyQuery = shouldIgnoreCompanySelector
                ? ""
                : `&${FILTER_NAMES.RootCompany}=${rootCompanyId}`;

            const filteredCompaniesQuery =
                !shouldIgnoreCompanySelector && filteredCompanies.length
                    ? `&${FILTER_NAMES.CompanyId}=${filteredCompanies}`
                    : "";

            const additionalQueryParams = additionalParams
                ? `&${additionalParams}`
                : "";

            const urlRoot = customTableName ?? tableName;

            const resource = `${urlRoot}${ENDPOINT_PATH_SEGMENTS.FilterList}?field=${filterName}${rootCompanyQuery}${filteredCompaniesQuery}${additionalQueryParams}`;

            const { data }: { data: Filter[] } = await getData(resource);

            if (isSubscribed) {
                const sortedOptions = handleFilterOptionsStructure(
                    data,
                    filterName,
                    tableName,
                    tasks,
                    t
                );

                setOptions(sortedOptions);
            }
        } catch (error) {
            if (isSubscribed && !isCanceled(error)) {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    return { isOpen, isLoading, options, setIsOpen };
};
